import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { auth, dbAccounts } from "../../../../backend/firebaseConfig";

const STATEMENTS_COLLECTION = "Monthly Commission Statements";
const timestampToIso = (value) =>
  value?.toDate ? value.toDate().toISOString() : value || null;
const statementRecord = (snapshot) => {
  const value = snapshot.data() || {};
  return {
    id: snapshot.id,
    ...value,
    generatedAt: timestampToIso(value.generatedAt),
    sentAt: timestampToIso(value.sentAt),
    latestGeneratedAt: timestampToIso(value.latestGeneratedAt),
    latestSentAt: timestampToIso(value.latestSentAt),
    readyAt: timestampToIso(value.readyAt),
    expiresAt: timestampToIso(value.expiresAt),
    updatedAt: timestampToIso(value.updatedAt),
  };
};

const API_URL = (
  process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
).replace(/\/$/, "");

const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000;
let cachedAuthToken = null;
let authTokenRequest = null;

const clearCachedAuthToken = () => {
  cachedAuthToken = null;
};

const getSharedAuthToken = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Authentication required.");

  const userId = user.uid || user.email || "current-user";
  const now = Date.now();
  if (
    !forceRefresh &&
    cachedAuthToken?.userId === userId &&
    cachedAuthToken.expiresAt - TOKEN_EXPIRY_BUFFER_MS > now
  ) {
    return cachedAuthToken.token;
  }

  if (authTokenRequest) return authTokenRequest;

  authTokenRequest = user.getIdTokenResult(forceRefresh)
    .then((result) => {
      const parsedExpiry = Date.parse(result.expirationTime);
      cachedAuthToken = {
        userId,
        token: result.token,
        expiresAt: Number.isFinite(parsedExpiry)
          ? parsedExpiry
          : Date.now() + (50 * 60 * 1000),
      };
      return result.token;
    })
    .finally(() => {
      authTokenRequest = null;
    });

  return authTokenRequest;
};

const authenticatedFetch = async (path, options, token) => fetch(
  `${API_URL}/api/monthly-commission-statements${path}`,
  {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  },
);

const request = async (path, options = {}) => {
  let token = await getSharedAuthToken();
  let response = await authenticatedFetch(path, options, token);
  if (response.status === 401) {
    clearCachedAuthToken();
    token = await getSharedAuthToken(true);
    response = await authenticatedFetch(path, options, token);
  }
  if (!response.ok) throw new Error("Statement request failed.");
  return response;
};

export const getMonthlyStatementConfiguration = async () => {
  const response = await request("/configuration");
  return (await response.json()).configuration || {};
};

export const getMonthlyStatementSummaries = async (incomePeriodId) => {
  const response = await request(`/${encodeURIComponent(incomePeriodId)}`);
  return (await response.json()).statements || [];
};

export const subscribeMonthlyStatementSummaries = (
  incomePeriodId,
  onData,
  onError,
) => onSnapshot(
  collection(
    dbAccounts,
    STATEMENTS_COLLECTION,
    incomePeriodId,
    "Advisers",
  ),
  (snapshot) => onData(snapshot.docs.map(statementRecord)),
  onError,
);

export const subscribeMonthlyStatementHistory = (
  incomePeriodId,
  adviserDocumentId,
  onData,
  onError,
) => onSnapshot(
  query(
    collection(
      dbAccounts,
      STATEMENTS_COLLECTION,
      incomePeriodId,
      "Advisers",
      adviserDocumentId,
      "Versions",
    ),
    orderBy("versionNumber", "desc"),
  ),
  (snapshot) => onData(snapshot.docs.map(statementRecord)),
  onError,
);

export const getMonthlyStatementHistory = async (incomePeriodId, adviserKey) => {
  const response = await request(
    `/${encodeURIComponent(incomePeriodId)}/${encodeURIComponent(adviserKey)}/history`,
  );
  return response.json();
};

export const storeMonthlyStatementVersion = async (values) => {
  const form = new FormData();
  form.append("adviserName", values.adviserName);
  form.append("month", values.month);
  form.append("recipient", values.recipient);
  if (values.statementCc) form.append("statementCc", values.statementCc);
  if (values.generatedByEmail) form.append("generatedByEmail", values.generatedByEmail);
  form.append("statementType", values.statementType);
  form.append("protectPdf", values.protectPdf ? "true" : "false");
  if (values.clientRequestId) form.append("clientRequestId", values.clientRequestId);
  if (values.adviserDocumentId) form.append("adviserDocumentId", values.adviserDocumentId);
  if (values.passwordEntityId) form.append("passwordEntityId", values.passwordEntityId);
  if (values.adviserDob) form.append("adviserDob", values.adviserDob);
  if (values.statementPassword) {
    form.append("statementPassword", values.statementPassword);
  }
  form.append("pdf", values.pdfBlob, values.pdfFileName);
  form.append("excel", values.excelBlob, values.excelFileName);
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/${encodeURIComponent(values.adviserKey)}/versions`,
    { method: "POST", body: form },
  );
  return (await response.json()).version;
};

export const sendMonthlyStatementVersion = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/${encodeURIComponent(values.adviserKey)}/${encodeURIComponent(values.versionId)}/send`,
    { method: "POST" },
  );
  return (await response.json()).version;
};

export const createMonthlyStatementSendBatch = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/batches`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requestId: values.requestId,
        entries: values.entries,
        monthKey: values.monthKey,
        monthLabel: values.monthLabel,
        sendMode: values.sendMode,
      }),
    },
  );
  return (await response.json()).job;
};

export const createMonthlyStatementDownloadArchive = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/downloads`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requestId: values.requestId,
        entries: values.entries,
        monthKey: values.monthKey,
        monthLabel: values.monthLabel,
      }),
    },
  );
  return (await response.json()).job;
};

export const subscribeMonthlyStatementDownloadArchive = (
  jobId,
  onData,
  onError,
) => onSnapshot(
  doc(dbAccounts, "Monthly Commission Statement Jobs", jobId),
  (snapshot) => onData(snapshot.exists() ? statementRecord(snapshot) : null),
  onError,
);

export const generateFreshMonthlyStatementFile = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/fresh/${values.fileType}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        adviserKey: values.adviserKey,
        monthKey: values.monthKey,
        identifier: values.identifier,
      }),
    },
  );
  return response.blob();
};

export const generateFreshMonthlyStatementVersion = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/fresh-version`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        adviserKey: values.adviserKey,
        monthKey: values.monthKey,
        identifier: values.identifier,
      }),
    },
  );
  return (await response.json()).version;
};

export const subscribeMonthlyStatementSendBatch = (
  jobId,
  onData,
  onError,
) => onSnapshot(
  doc(dbAccounts, "Monthly Commission Statement Jobs", jobId),
  (snapshot) => onData(snapshot.exists() ? statementRecord(snapshot) : null),
  onError,
);

export const subscribeMonthlyStatementSendBatchItems = (
  jobId,
  onData,
  onError,
) => onSnapshot(
  collection(
    dbAccounts,
    "Monthly Commission Statement Jobs",
    jobId,
    "Items",
  ),
  (snapshot) => onData(snapshot.docs.map(statementRecord)),
  onError,
);

export const getMonthlyStatementSendBatch = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/batches/${encodeURIComponent(values.jobId)}`,
  );
  return (await response.json()).job;
};

export const getStoredMonthlyStatementFile = async (values) => {
  const response = await request(
    `/${encodeURIComponent(values.incomePeriodId)}/${encodeURIComponent(values.adviserKey)}/${encodeURIComponent(values.versionId)}/${values.fileType}`,
  );
  return response.blob();
};
